import { anyRoomListName } from 'owa-locstrings/lib/strings/anyroomlistname.locstring.json';
import loc from 'owa-localize';
import { makeGetRequest } from 'owa-ows-gateway';
import type PlacesFilters from '../schema/PlacesFilters';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import type { Room, RoomListResponse } from 'owa-location-service';
import { createRequestQueue, RequestQueue } from 'owa-service-utils';
import { isFeatureEnabled } from 'owa-feature-flags';

const ROOM_LIST_BATCH_SIZE = 500;
const ROOM_BATCH_SIZE = 100;
const MAXIMUM_NUM_REQUESTS = 5;

/**
 * Performs a request to the Places API to fetch all the room lists
 */
export function getRoomListsVNext(city?: string): Promise<RoomListResponse | null> {
    let endpoint: string = isFeatureEnabled('cal-mf-placesb2')
        ? `locationsb2/odata/api/v2.0/places/microsoft.exchange.locationsAndTime.roomList/?$top=${ROOM_LIST_BATCH_SIZE}&$orderby=displayName`
        : `locations/odata/api/v2.0/places/microsoft.exchange.locationsAndTime.roomList/?$top=${ROOM_LIST_BATCH_SIZE}&$orderby=displayName`;
    if (city) {
        endpoint += `&$filter=tolower(Address/City) eq '${encodeURIComponent(city.toLowerCase())}'`;
    }
    return getResults(endpoint, ROOM_LIST_BATCH_SIZE, true);
}

/**
 * Performs a request to the Places API to search for rooms based on the provided filters
 * @param placesFilters: The room filters
 */
export function searchRoomsVNext(placesFilters: PlacesFilters = null): Promise<Room[]> {
    const {
        roomListEmails = null,
        shouldGetSpaces = false,
        cities = [],
        hasRoomLists = false,
    } = placesFilters;
    let isPaginationEnabled = false;
    let endpoint = isFeatureEnabled('cal-mf-placesb2')
        ? 'locationsb2/odata/api/v2.0/Places'
        : 'locations/odata/api/v2.0/Places';

    if (
        roomListEmails?.length === 1 &&
        roomListEmails[0] &&
        roomListEmails[0] !== loc(anyRoomListName)
    ) {
        // Optimization: If there's only one RL in the filters, query that particular RL and expand the rooms or spaces
        // This call is more performant than querying for all rooms for which the RL matches.
        isPaginationEnabled = true;
        endpoint += `('${roomListEmails[0]}')/microsoft.exchange.locationsAndTime.Roomlist/${
            shouldGetSpaces ? 'Spaces' : 'Rooms'
        }/?$orderBy=displayName&$top=${ROOM_BATCH_SIZE}`;
    } else {
        // All other queries for rooms or spaces. Here we also check if any filters are present.
        // If so we use the normal batch size else when firing a blank query in case of no filters
        // we query for only 20 rooms to avoid many getAvailability calls.
        endpoint += `/microsoft.exchange.locationsAndTime.${
            shouldGetSpaces ? 'Space' : 'Room'
        }/?$top=${roomListEmails?.length > 1 || cities.length > 0 ? ROOM_BATCH_SIZE : 20}`;
    }

    let filterParts: string[] = [];

    if (placesFilters) {
        if (roomListEmails?.length > 1 && roomListEmails.some(x => x != loc(anyRoomListName))) {
            let roomListsfilterParts: string[] = [];
            // Search by RoomList name for more than one roomlist.  For instance if the filter value is ["Building 34", "Building 35"] the filter part
            // would look like
            // &$filter=(roomLists/any(roomList: roomList eq 'b34rooms%40microsoft.com') or roomLists/any(roomList: roomList eq 'b35rooms%40microsoft.com'))"
            roomListEmails.forEach(roomList => {
                roomListsfilterParts.push(
                    `roomLists/any(roomList: roomList eq '${encodeURIComponent(roomList)}')`
                );
            });
            filterParts.push(`(${roomListsfilterParts.join(' or ')})`);
        }

        if (cities && cities.length > 0) {
            // Search by City.  For instance if the filter value is "Redmond" the filter part
            // would look like "contains(tolower(Address/City), 'redmond')"
            let citiesFilterParts: string[] = [];
            cities.forEach(city => {
                citiesFilterParts.push(
                    `contains(tolower(Address/City), '${encodeURIComponent(city.toLowerCase())}')`
                );
            });

            filterParts.push(`(${citiesFilterParts.join(' or ')})`);
        }
    }

    if (filterParts.length > 0) {
        // If filters were specified, construct the final URL by concatenating all filter parts
        endpoint += '&$filter=' + filterParts.join(' or ');
    }

    if (hasRoomLists) {
        endpoint += 'and (roomLists/any(r:r ne null))&$orderby=displayName';
    }

    return getResults(endpoint, ROOM_BATCH_SIZE, isPaginationEnabled).then(searchRoomsResult => {
        if (searchRoomsResult?.value) {
            return searchRoomsResult.value;
        }
        return [];
    });
}

/**
 * Performs mutiple requests (if necessary) to the Places V2 API in batches and concatenates the results to return the total response
 * TODO: Create a store object with results so we can render things on the fly as we get responses https://outlookweb.visualstudio.com/Outlook%20Web/_workitems/edit/90142
 */
async function getResults(
    endpoint: string,
    batchSize: number,
    isPaginationEnabled: boolean
): Promise<RoomListResponse | null> {
    const requestQueue: RequestQueue = createRequestQueue(MAXIMUM_NUM_REQUESTS);
    endpoint += '&$count=true';
    let primaryResponse = await makeGetRequest(
        endpoint,
        undefined,
        true,
        undefined,
        undefined,
        undefined,
        'roomLists'
    );
    if (!isSuccessStatusCode(primaryResponse.status)) {
        return null;
    }

    primaryResponse = await primaryResponse.json();
    let currentNumValues = primaryResponse.value?.length;
    let counter = 1;
    let totalNumValues = primaryResponse['@odata.count'];
    if (!primaryResponse.value || currentNumValues === totalNumValues || !isPaginationEnabled) {
        return primaryResponse;
    }

    const requestResponseArray = [];
    while (currentNumValues < totalNumValues) {
        requestResponseArray.push(`${endpoint}&$skip=${batchSize * counter++}`);
        currentNumValues += batchSize;
    }

    await Promise.all(
        requestResponseArray.map((request, index) => {
            return requestQueue.add(() => {
                return makeGetRequest(
                    request,
                    undefined,
                    true,
                    undefined,
                    undefined,
                    undefined,
                    'roomLists'
                )
                    .then(response => {
                        return response.json();
                    })
                    .then(batchResponse => {
                        requestResponseArray[index] = batchResponse.value;
                    })
                    .catch(() => {
                        return null;
                    });
            });
        })
    );

    requestResponseArray.map(response => {
        primaryResponse.value = primaryResponse.value.concat(response);
    });

    return primaryResponse;
}
